import { Link } from "react-router-dom";
import { classifyViews, useEntitlements } from "@cloudox/api-client";
import { ViewCard } from "@cloudox/ui";
import { DemoNotice } from "@/components/DemoNotice";
import { useKnowledgeContext } from "@/components/layout/KnowledgeShell";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { formatGeneratedAt, partitionViews, relatedPagePath, viewPath } from "@/lib/preview";

/**
 * The preview's landing page.
 *
 * `/` used to redirect straight into the first section of one narrated view, so
 * a first-time visitor met a mid-document paragraph with no statement of what
 * they were looking at, no explanation of what a Knowledge View is, and no way
 * to discover the three intelligence pages this run also published.
 *
 * Everything here is read from the published navigation manifest. Nothing is
 * counted, re-derived or hardcoded: the resource, account and Region totals a
 * landing page would like to show are produced by the counting kernel with an
 * explicit basis (ADR-0007) and are not part of the public bundle, so they stay
 * where the run wrote them — inside the report prose that states their basis.
 */
export function OverviewPage() {
  const { nav } = useKnowledgeContext();
  const entitlements = useEntitlements();
  useDocumentTitle("Example AWS discovery report");

  const { available } = classifyViews(nav, entitlements);
  const { lenses, reference } = partitionViews(available);
  const generated = formatGeneratedAt(nav.generatedAt);

  return (
    <div className="space-y-10">
      <header>
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.12em]"
          style={{ color: "var(--fg-subtle)" }}
        >
          Public example
        </p>
        <h1 className="mt-1.5 text-2xl font-semibold sm:text-3xl" style={{ color: "var(--fg)" }}>
          An AWS environment, explained
        </h1>
        <p
          className="mt-3 max-w-2xl text-[15px] leading-relaxed"
          style={{ color: "var(--fg-muted)" }}
        >
          CloudoX discovered a real AWS environment, built a knowledge graph from provider-native
          evidence, interpreted it deterministically, and wrote this report. Every claim below
          traces back to something the run actually observed — where the evidence was missing, the
          report says so instead of guessing.
        </p>
        <div className="mt-4">
          <DemoNotice />
        </div>
      </header>

      <section aria-labelledby="lenses-heading">
        <h2 id="lenses-heading" className="text-lg font-semibold" style={{ color: "var(--fg)" }}>
          Start with an audience
        </h2>
        <p className="mt-1.5 max-w-2xl text-sm" style={{ color: "var(--fg-muted)" }}>
          A Knowledge View is a lens over one interpreted knowledge base. Each audience gets
          different explanations and priorities — never different facts.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {lenses.map((view) => (
            <ViewCard key={view.key} view={view} href={viewPath(view.key)} />
          ))}
        </div>
      </section>

      {reference ? (
        <section aria-labelledby="reference-heading">
          <h2
            id="reference-heading"
            className="text-lg font-semibold"
            style={{ color: "var(--fg)" }}
          >
            Or go straight to the evidence
          </h2>
          <Link
            to={viewPath(reference.key)}
            className="card mt-4 block transition hover:border-[color:var(--accent)]"
            style={{ textDecoration: "none" }}
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-sm font-semibold" style={{ color: "var(--fg)" }}>
                {reference.displayName} tables
              </h3>
              <span className="text-xs" style={{ color: "var(--fg-subtle)" }}>
                {reference.sectionCount} table
                {reference.sectionCount === 1 ? "" : "s"}
              </span>
            </div>
            <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
              Routing, connectivity, ingress rules, identity and trust, public exposure,
              dependencies, DNS, encryption and tagging — derived directly from the graph with no
              narration. Confidence is carried per row rather than per document, which is why the
              view itself claims none.
            </p>
          </Link>
        </section>
      ) : null}

      {nav.relatedPages.length > 0 ? (
        <section aria-labelledby="layers-heading">
          <h2 id="layers-heading" className="text-lg font-semibold" style={{ color: "var(--fg)" }}>
            Supporting intelligence layers
          </h2>
          <p className="mt-1.5 max-w-2xl text-sm" style={{ color: "var(--fg-muted)" }}>
            Cross-cutting analyses this run wrote beside the views. They read the same knowledge
            graph; they are not a seventh audience.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {nav.relatedPages.map((page) => (
              <Link
                key={page.key}
                to={relatedPagePath(page.key)}
                className="card block transition hover:border-[color:var(--accent)]"
                style={{ textDecoration: "none" }}
              >
                <h3 className="text-sm font-semibold" style={{ color: "var(--fg)" }}>
                  {page.title}
                </h3>
                {page.description ? (
                  <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
                    {page.description}
                  </p>
                ) : null}
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section
        aria-labelledby="provenance-heading"
        className="rounded-lg border px-4 py-4"
        style={{
          borderColor: "var(--border)",
          backgroundColor: "var(--bg-elevated)",
        }}
      >
        <h2
          id="provenance-heading"
          className="text-sm font-semibold"
          style={{ color: "var(--fg)" }}
        >
          About this report
        </h2>
        <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
          {generated ? (
            <div>
              <dt style={{ color: "var(--fg-subtle)" }}>Report generated</dt>
              <dd className="mt-0.5" style={{ color: "var(--fg)" }}>
                {generated}
              </dd>
            </div>
          ) : null}
          <div>
            <dt style={{ color: "var(--fg-subtle)" }}>Prose</dt>
            <dd className="mt-0.5" style={{ color: "var(--fg)" }}>
              {nav.narrated ? "AI-narrated from deterministic views" : "Deterministically rendered"}
            </dd>
          </div>
          <div>
            <dt style={{ color: "var(--fg-subtle)" }}>Cloud</dt>
            <dd className="mt-0.5" style={{ color: "var(--fg)" }}>
              {nav.provider.toUpperCase()}
            </dd>
          </div>
        </dl>
        <p className="mt-3 text-xs" style={{ color: "var(--fg-subtle)" }}>
          The date above is when these report artifacts were written, which is not the same as when
          the environment was discovered. Resource, account and Region counts are stated inside the
          report together with the basis that defines them.
        </p>
      </section>

      <section
        aria-labelledby="cta-heading"
        className="flex flex-wrap items-center justify-between gap-4 border-t pt-6"
        style={{ borderColor: "var(--border)" }}
      >
        <div>
          <h2 id="cta-heading" className="text-sm font-semibold" style={{ color: "var(--fg)" }}>
            Want this for your own environment?
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            CloudoX reads your account through a read-only role. Nothing is written, and no
            credentials are stored.
          </p>
        </div>
        <a
          href="https://cloudox.io"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary"
        >
          Learn more at cloudox.io
        </a>
      </section>
    </div>
  );
}
